import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Button,
  Input,
  Loader,
  Message,
  useToaster,
  Panel,
  IconButton,
  Checkbox,
  TimePicker,
  DatePicker,
  InputNumber,
  Toggle
} from 'rsuite';
import { useTranslation } from 'react-i18next';
import CheckIcon from '@rsuite/icons/Check';
import PlusIcon from '@rsuite/icons/Plus';
import TrashIcon from '@rsuite/icons/Trash';
import { v4 as uuidv4 } from 'uuid';
import { getCrons, manageCron, deleteCron, getCronsByType } from '@/services/cron.service';
import { fetchSetting, updateSetting } from '@/services/setting.service';
import { useDispatch } from 'react-redux';
import Textarea from '@/components/Textarea';

interface Cron {
  id: string;
  type: string;
  schedule: {
    minute: string;
    hour: string;
    dayOfMonth: string;
    month: string;
    dayOfWeek: string;
  };
  fieldTitle: string;
  message?: string;
  enabled: boolean;
  inactiveDays?: number;
  isModified?: boolean;
}

interface PasswordSettings {
  _id?: string;
  maxAttempt: { value: number | undefined; enable: boolean };
  expiryConf: { value: number | undefined; enable: boolean };
  sessionExpiryDays: { value: number | undefined; enable: boolean };
  isModified?: boolean;
}

interface NotificationSettingsDrawerProps {
  open: boolean;
  onClose: () => void;
  type: 'Leave' | 'WFH' | 'block_users';
}

const NotificationSettingsDrawer: React.FC<NotificationSettingsDrawerProps> = ({
  open,
  onClose,
  type
}) => {
  const { t } = useTranslation();
  const toaster = useToaster();
  const dispatch = useDispatch();
  const [crons, setCrons] = useState<Cron[]>([]);
  const [initialCrons, setInitialCrons] = useState<Cron[]>([]);
  const [openPanels, setOpenPanels] = useState<Map<string, boolean>>(new Map());
  const [expandedPanels, setExpandedPanels] = useState<Map<string, boolean>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [passwordSettings, setPasswordSettings] = useState<PasswordSettings>({
    maxAttempt: { value: undefined, enable: true },
    expiryConf: { value: undefined, enable: true },
    sessionExpiryDays: { value: undefined, enable: true },
    isModified: false
  });
  const [initialPasswordSettings, setInitialPasswordSettings] = useState<PasswordSettings>({
    maxAttempt: { value: undefined, enable: true },
    expiryConf: { value: undefined, enable: true },
    sessionExpiryDays: { value: undefined, enable: true },
    isModified: false
  });

  useEffect(() => {
    if (open) {
      setIsLoading(true);
      fetchSetting('login_Config')
        .then(data => {
          if (typeof data === 'string') {
            console.error('fetchSetting returned string:', data);
            toaster.push(
              <Message type="error" duration={5000} showIcon>
                {t(data)}
              </Message>,
              { placement: 'topCenter' }
            );
            return;
          }
          const settings = {
            _id: data._id,
            maxAttempt: {
              value: data.maxAttempt?.value || 3,
              enable: data.maxAttempt?.enable !== undefined ? data.maxAttempt.enable : true
            },
            expiryConf: {
              value: data.expiryConf?.value || 3,
              enable: data.expiryConf?.enable !== undefined ? data.expiryConf.enable : true
            },
            sessionExpiryDays: {
              value: data.sessionExpiryDays?.value || 30,
              enable:
                data.sessionExpiryDays?.enable !== undefined ? data.sessionExpiryDays.enable : true
            },
            isModified: false
          };
          setPasswordSettings(settings);
          setInitialPasswordSettings(JSON.parse(JSON.stringify(settings)));
        })
        .catch(err => {
          console.error('Error fetching login_Config:', err);
          toaster.push(
            <Message type="error" duration={5000} showIcon>
              {t(err || 'Failed to load login settings')}
            </Message>,
            { placement: 'topCenter' }
          );
        });

      // Fetch crons
      dispatch(getCronsByType(type))
        .then(data => {
          if (typeof data === 'string') {
            console.error('getCrons returned string:', data);
            toaster.push(
              <Message type="error" duration={5000} showIcon>
                {t(data)}
              </Message>,
              { placement: 'topCenter' }
            );
            setCrons([]);
            setInitialCrons([]);
            return;
          }
          const filteredCrons = Array.isArray(data)
            ? data
                .filter(
                  cron =>
                    cron &&
                    (cron.type === type || (type === 'block_users' && cron.type === 'untraced'))
                )
                .map(cron => ({
                  id: cron._id?.toString() || uuidv4(),
                  type: cron.type || type,
                  schedule: {
                    minute: cron.schedule?.minute || '*',
                    hour: cron.schedule?.hour || '*',
                    dayOfMonth: cron.schedule?.dayOfMonth || '*',
                    month: cron.schedule?.month || '*',
                    dayOfWeek: cron.schedule?.dayOfWeek || '*'
                  },
                  fieldTitle: cron.fieldTitle || '',
                  message: cron.message || '',
                  enabled: cron.enabled !== undefined ? cron.enabled : true,
                  inactiveDays: cron.type === 'block_users' ? cron.inactiveDays : undefined,
                  isModified: false
                }))
            : [];
          setCrons(filteredCrons);
          setInitialCrons(JSON.parse(JSON.stringify(filteredCrons)));
          const newOpenPanels = new Map<string, boolean>();
          const newExpandedPanels = new Map<string, boolean>();
          filteredCrons.forEach(cron => {
            newOpenPanels.set(cron.id, true);
            newExpandedPanels.set(cron.id, true);
          });
          setOpenPanels(newOpenPanels);
          setExpandedPanels(newExpandedPanels);
        })
        .catch(err => {
          console.error('Error fetching crons:', err.message);
          toaster.push(
            <Message type="error" duration={5000} showIcon>
              {t(err.message || 'Failed to load notifications')}
            </Message>,
            { placement: 'topCenter' }
          );
          setCrons([]);
          setInitialCrons([]);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setCrons([]);
      setInitialCrons([]);
      setOpenPanels(new Map());
      setExpandedPanels(new Map());
      setPasswordSettings({
        maxAttempt: { value: 3, enable: true },
        expiryConf: { value: 3, enable: true },
        sessionExpiryDays: { value: 30, enable: true },
        isModified: false
      });
      setInitialPasswordSettings({
        maxAttempt: { value: 3, enable: true },
        expiryConf: { value: 3, enable: true },
        sessionExpiryDays: { value: 30, enable: true },
        isModified: false
      });
    }
  }, [open, t, toaster, type, dispatch]);

  const addCron = (cronType: string) => {
    const newId = uuidv4();
    const newCron = {
      id: newId,
      type: cronType,
      schedule: { minute: '*', hour: '*', dayOfMonth: '*', month: '*', dayOfWeek: '*' },
      fieldTitle: '',
      message: cronType !== 'block_users' ? '' : undefined,
      enabled: true,
      inactiveDays: cronType === 'block_users' ? 30 : undefined,
      isModified: true
    };
    setCrons(prev => [...prev, newCron]);
    setOpenPanels(prev => new Map(prev).set(newId, true));
    setExpandedPanels(prev => new Map(prev).set(newId, true));
  };

  const updateCron = (id: string, field: keyof Cron | string, value: any) => {
    setCrons(prev =>
      prev.map(c => {
        if (c.id === id) {
          if (field.startsWith('schedule.')) {
            const scheduleField = field.split('.')[1] as keyof Cron['schedule'];
            return { ...c, schedule: { ...c.schedule, [scheduleField]: value }, isModified: true };
          }
          return { ...c, [field]: value, isModified: true };
        }
        return c;
      })
    );
  };

  const updatePasswordSetting = (
    field: keyof Omit<PasswordSettings, '_id' | 'isModified'>,
    key: 'value' | 'enable',
    value: any
  ) => {
    setPasswordSettings(prev => {
      const newSettings = {
        ...prev,
        [field]: { ...prev[field], [key]: value },
        isModified: true
      };
      if (key === 'enable' && value === true && prev[field].value === undefined) {
        // Set default value when enabling
        newSettings[field].value =
          field === 'sessionExpiryDays' ? 30 : field === 'expiryConf' ? 7 : 5;
      } else if (key === 'enable' && value === false) {
        // Clear value when disabling
        newSettings[field].value = undefined;
      }
      return newSettings;
    });
  };

  const togglePanel = (id: string) => {
    setOpenPanels(prev => {
      const newPanels = new Map(prev);
      newPanels.delete(id);
      setCrons(prev => prev.filter(c => c.id !== id));
      return newPanels;
    });
  };

  const toggleExpanded = (id: string) => {
    setExpandedPanels(prev => {
      const newPanels = new Map(prev);
      newPanels.set(id, !newPanels.get(id));
      return newPanels;
    });
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const validationErrors: string[] = [];

      if (passwordSettings.isModified) {
        if (
          passwordSettings.maxAttempt.enable &&
          (passwordSettings.maxAttempt.value === undefined || passwordSettings.maxAttempt.value < 0)
        ) {
          validationErrors.push(t('Number of Login Attempts must be at least 0 when enabled'));
        }
        if (
          passwordSettings.expiryConf.enable &&
          (passwordSettings.expiryConf.value === undefined || passwordSettings.expiryConf.value < 0)
        ) {
          validationErrors.push(t('Password Expiry days must be at least 0 when enabled'));
        }
        if (
          passwordSettings.sessionExpiryDays.enable &&
          (passwordSettings.sessionExpiryDays.value === undefined ||
            passwordSettings.sessionExpiryDays.value < 0)
        ) {
          validationErrors.push(t('Automatic Expiry days must be at least 0 when enabled'));
        }
      }

      const deletedCrons = initialCrons.filter(init => !crons.some(curr => curr.id === init.id));

      // const modifiedCrons = type === 'block_users' ? crons.filter(cron => cron.isModified) : [];
      const modifiedCrons = crons.filter(cron => cron.isModified);
      modifiedCrons.forEach((cron, index) => {
        if (!cron.fieldTitle) {
          validationErrors.push(`Notification ${index + 1}: ${t('Title is required')}`);
        }
        if (cron.type === 'block_users' && (!cron.inactiveDays || cron.inactiveDays < 1)) {
          validationErrors.push(
            `Cron ${cron.id}: ${t('Inactive days must be at least 1 for block users')}`
          );
        }
      });

      if (validationErrors.length > 0) {
        toaster.push(
          <Message type="error" duration={5000} showIcon>
            {validationErrors.join('; ')}
          </Message>,
          { placement: 'topCenter' }
        );
        setIsLoading(false);
        return;
      }

      if (passwordSettings.isModified) {
        await updateSetting(passwordSettings._id, {
          name: 'login_Config',
          maxAttempt: passwordSettings.maxAttempt,
          expiryConf: passwordSettings.expiryConf,
          sessionExpiryDays: passwordSettings.sessionExpiryDays
        });
      }

      // if (type === 'block_users') {
      const deletePromises = deletedCrons
        .filter(cron => !cron.id.includes('-'))
        .map(cron => {
          console.log(`Deleting cron: ${cron.id}`);
          return dispatch(deleteCron(cron.id));
        });

      const deleteResults = await Promise.all(deletePromises);
      const deleteErrors = deleteResults.filter(result => typeof result === 'string');
      if (deleteErrors.length > 0) {
        console.error('Some notifications failed to delete:', deleteErrors);
        toaster.push(
          <Message type="error" duration={5000} showIcon>
            {t(deleteErrors.join('; '))}
          </Message>,
          { placement: 'topCenter' }
        );
        setIsLoading(false);
        return;
      }

      const savePromises = modifiedCrons.map(async cron => {
        const cronData = {
          _id: cron.id.includes('-') ? undefined : cron.id,
          type: cron.type,
          schedule: {
            minute: cron.schedule.minute,
            hour: cron.schedule.hour,
            dayOfMonth: cron.schedule.dayOfMonth,
            month: cron.schedule.month,
            dayOfWeek: cron.schedule.dayOfWeek
          },
          fieldTitle: cron.fieldTitle,
          message: cron.message,
          enabled: cron.enabled,
          inactiveDays: cron.type === 'block_users' ? cron.inactiveDays : undefined
        };
        console.log(`Saving cron: ${JSON.stringify(cronData)}`);
        return dispatch(manageCron(cronData));
      });

      const saveResults = await Promise.all(savePromises);
      const saveErrors = saveResults.filter(result => typeof result === 'string');

      if (saveErrors.length > 0) {
        console.error('Some notifications failed to save:', saveErrors);
        toaster.push(
          <Message type="error" duration={5000} showIcon>
            {t(saveErrors.join('; '))}
          </Message>,
          { placement: 'topCenter' }
        );
        setIsLoading(false);
        return;
      }
      // }

      toaster.push(
        <Message type="success" duration={5000} showIcon>
          {t('Updated Successfully')}
        </Message>,
        { placement: 'topCenter' }
      );
      setInitialCrons(JSON.parse(JSON.stringify(crons)));
      setInitialPasswordSettings(JSON.parse(JSON.stringify(passwordSettings)));
      setPasswordSettings(prev => ({ ...prev, isModified: false }));
      onClose();
    } catch (err) {
      console.error('Error saving settings:', err.message);
      toaster.push(
        <Message type="error" duration={5000} showIcon>
          {t(err.message || 'Error saving settings')}
        </Message>,
        { placement: 'topCenter' }
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <style>
        {`
          .rs-picker-date-menu .rs-calendar-header-year,
          .rs-picker-date-menu .rs-calendar-header-year-btn {
            display: none !important;
          }
          .section-divider {
            margin: 30px 0;
            padding: 15px 0;
            background-color: #f5f5f5;
            border-radius: 4px;
            text-align: center;
          }
          .section-divider h5 {
            margin: 0;
            font-weight: bold;
            color: #333;
          }
        `}
      </style>
      <Drawer open={open} onClose={onClose} size="sm">
        <Drawer.Header>
          <Drawer.Title>
            {t(type === 'block_users' ? 'Settings' : 'Notification Settings')}
          </Drawer.Title>
          <Drawer.Actions>
            <IconButton
              disabled={isLoading}
              loading={isLoading}
              onClick={handleSave}
              appearance="primary"
              icon={<CheckIcon />}
            >
              {t('Save')}
            </IconButton>

            <Button onClick={onClose} appearance="subtle">
              {t('Cancel')}
            </Button>
          </Drawer.Actions>
        </Drawer.Header>
        <Drawer.Body>
          {isLoading ? (
            <Loader content={t('Loading data...')} />
          ) : (
            <>
              {type == 'block_users' && (
                <>
                  <div className="section-divider">
                    <h5>{t('Login Management Settings')}</h5>
                  </div>

                  <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center' }}>
                    <label style={{ marginRight: 10, width: 200 }}>
                      {t('Mobile Automatic Expiration')}
                    </label>
                    <Toggle
                      checked={passwordSettings.sessionExpiryDays.enable}
                      onChange={checked =>
                        updatePasswordSetting('sessionExpiryDays', 'enable', checked)
                      }
                      checkedChildren={t('Enabled')}
                      unCheckedChildren={t('Disabled')}
                    />
                    {passwordSettings.sessionExpiryDays.enable && (
                      <InputNumber
                        value={passwordSettings.sessionExpiryDays.value}
                        onChange={value => {
                          const parsedValue = value === '' ? '' : parseInt(value);
                          updatePasswordSetting(
                            'sessionExpiryDays',
                            'value',
                            isNaN(parsedValue) ? '' : parsedValue
                          );
                        }}
                        placeholder={t('Enter session expiry days (e.g., 30)')}
                        min={0}
                        style={{ marginLeft: 10, width: 200 }}
                        postfix={t('days')}
                      />
                    )}
                  </div>
                  <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center' }}>
                    <label style={{ marginRight: 10, width: 200 }}>{t('Password Expiry')}</label>
                    <Toggle
                      checked={passwordSettings.expiryConf.enable}
                      onChange={checked => updatePasswordSetting('expiryConf', 'enable', checked)}
                      checkedChildren={t('Enabled')}
                      unCheckedChildren={t('Disabled')}
                    />
                    {passwordSettings.expiryConf.enable && (
                      <InputNumber
                        value={passwordSettings.expiryConf.value}
                        onChange={value => {
                          const parsedValue = value === '' ? '' : parseInt(value);
                          updatePasswordSetting(
                            'expiryConf',
                            'value',
                            isNaN(parsedValue) ? '' : parsedValue
                          );
                        }}
                        placeholder={t('Enter expiry days')}
                        min={0}
                        style={{ marginLeft: 10, width: 200 }}
                        postfix={t('days')}
                      />
                    )}
                  </div>
                  <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center' }}>
                    <label style={{ marginRight: 10, width: 200 }}>
                      {t('Number of Login Attempts')}
                    </label>
                    <Toggle
                      checked={passwordSettings.maxAttempt.enable}
                      onChange={checked => updatePasswordSetting('maxAttempt', 'enable', checked)}
                      checkedChildren={t('Enabled')}
                      unCheckedChildren={t('Disabled')}
                    />
                    {passwordSettings.maxAttempt.enable && (
                      <InputNumber
                        value={passwordSettings.maxAttempt.value}
                        onChange={value => {
                          const parsedValue = value === '' ? '' : parseInt(value);
                          updatePasswordSetting(
                            'maxAttempt',
                            'value',
                            isNaN(parsedValue) ? '' : parsedValue
                          );
                        }}
                        placeholder={t('Enter max login attempts (e.g., 5)')}
                        min={0}
                        style={{ marginLeft: 10, width: 200 }}
                        postfix={t('attempts')}
                      />
                    )}
                  </div>
                </>
              )}
              {type === 'block_users' && (
                <>
                  <div className="section-divider">
                    <h5>{t('Block Users Settings')}</h5>
                  </div>
                  {/* <div style={{ marginBottom: 10 }}>
                    <IconButton
                      icon={<PlusIcon />}
                      onClick={() => addCron(type)}
                      appearance="subtle"
                      size="sm"
                    >
                      {t('Add Block Rule')}
                    </IconButton>
                  </div> */}
                  {crons
                    .filter(cron => cron.type === 'block_users')
                    .map(
                      (cron, index) =>
                        openPanels.get(cron.id) && (
                          <Panel
                            key={cron.id}
                            header={`${t('Auto Block Inactive Accounts')}`}
                            collapsible
                            expanded={expandedPanels.get(cron.id)}
                            onSelect={() => toggleExpanded(cron.id)}
                            style={{ marginBottom: 10 }}
                          >
                            <div style={{ marginBottom: 20 }}>
                              <label>{t('Title')}</label>
                              <Input
                                value={cron.fieldTitle}
                                onChange={value => updateCron(cron.id, 'fieldTitle', value)}
                                placeholder={t('Enter title')}
                              />
                            </div>
                            <div style={{ marginBottom: 20 }}>
                              <label>{t('Inactive Days')}</label>
                              <Input
                                type="number"
                                value={cron.inactiveDays || ''}
                                onChange={value =>
                                  updateCron(cron.id, 'inactiveDays', parseInt(value) || undefined)
                                }
                                placeholder={t('Enter inactive days (e.g., 15)')}
                                min={1}
                              />
                            </div>
                            <div style={{ marginBottom: 20 }}>
                              <label>{t('Schedule Time')}</label>
                              <TimePicker
                                format="HH:mm"
                                placement="auto"
                                value={
                                  cron.schedule.hour !== '*' && cron.schedule.minute !== '*'
                                    ? new Date(
                                        0,
                                        0,
                                        0,
                                        Number(cron.schedule.hour),
                                        Number(cron.schedule.minute)
                                      )
                                    : null
                                }
                                onChange={value => {
                                  if (value) {
                                    updateCron(
                                      cron.id,
                                      'schedule.hour',
                                      value.getHours().toString()
                                    );
                                    updateCron(
                                      cron.id,
                                      'schedule.minute',
                                      value.getMinutes().toString()
                                    );
                                  } else {
                                    updateCron(cron.id, 'schedule.hour', '*');
                                    updateCron(cron.id, 'schedule.minute', '*');
                                  }
                                }}
                                placeholder={t('Select time')}
                                style={{ width: '100%' }}
                              />
                            </div>
                            <div style={{ marginBottom: 20 }}>
                              <label>{t('Schedule Day and Month (optional)')}</label>
                              <DatePicker
                                format="d MMM"
                                placement="auto"
                                value={
                                  cron.schedule.dayOfMonth !== '*' && cron.schedule.month !== '*'
                                    ? new Date(
                                        2025,
                                        Number(cron.schedule.month) - 1,
                                        Number(cron.schedule.dayOfMonth)
                                      )
                                    : null
                                }
                                onChange={value => {
                                  if (value) {
                                    updateCron(
                                      cron.id,
                                      'schedule.dayOfMonth',
                                      value.getDate().toString()
                                    );
                                    updateCron(
                                      cron.id,
                                      'schedule.month',
                                      (value.getMonth() + 1).toString()
                                    );
                                  } else {
                                    updateCron(cron.id, 'schedule.dayOfMonth', '*');
                                    updateCron(cron.id, 'schedule.month', '*');
                                  }
                                }}
                                placeholder={t('Select day and month (if needed)')}
                                style={{ width: '100%' }}
                                ranges={[]}
                                cleanable={true}
                              />
                            </div>
                            <div style={{ marginBottom: 20 }}>
                              <Toggle
                                checked={cron.enabled}
                                onChange={(value: boolean) => updateCron(cron.id, 'enabled', value)}
                                checkedChildren={t('Enabled')}
                                unCheckedChildren={t('Disabled')}
                              />
                            </div>
                            {/* <IconButton
                              icon={<TrashIcon />}
                              onClick={() => togglePanel(cron.id)}
                              appearance="subtle"
                              size="sm"
                              style={{ marginTop: 10 }}
                            >
                              {t('Delete Block Rule')}
                            </IconButton> */}
                          </Panel>
                        )
                    )}
                </>
              )}
              {type == 'WFH' && (
                <div>
                  {crons.map(
                    (cron, index) =>
                      openPanels.get(cron.id) && (
                        <Panel
                          key={cron.id}
                          header={
                            crons.length > 0
                              ? `${t('Manager Validation Reminder')} ${index + 1}`
                              : t('Manager Validation Reminder')
                          }
                          collapsible
                          expanded={expandedPanels.get(cron.id)}
                          onSelect={() => toggleExpanded(cron.id)}
                          style={{ marginBottom: 10 }}
                        >
                          <div style={{ marginBottom: 20 }}>
                            <label>{t('Title')}</label>
                            <Input
                              value={cron.fieldTitle}
                              onChange={value => updateCron(cron.id, 'fieldTitle', value)}
                              placeholder={t('Enter title')}
                            />
                          </div>
                          <div style={{ marginBottom: 20 }}>
                            <label>{t('Message')}</label>
                            <Textarea
                              value={cron.message || ''}
                              onChange={value => updateCron(cron.id, 'message', value)}
                              placeholder={t('Enter notification message')}
                              block
                            />
                          </div>
                          <div style={{ marginBottom: 20 }}>
                            <label>{t('Schedule Time')}</label>
                            <TimePicker
                              format="HH:mm"
                              placement="auto"
                              value={
                                cron.schedule.hour !== '*' && cron.schedule.minute !== '*'
                                  ? new Date(
                                      0,
                                      0,
                                      0,
                                      Number(cron.schedule.hour),
                                      Number(cron.schedule.minute)
                                    )
                                  : null
                              }
                              onChange={value => {
                                if (value) {
                                  updateCron(cron.id, 'schedule.hour', value.getHours().toString());
                                  updateCron(
                                    cron.id,
                                    'schedule.minute',
                                    value.getMinutes().toString()
                                  );
                                } else {
                                  updateCron(cron.id, 'schedule.hour', '*');
                                  updateCron(cron.id, 'schedule.minute', '*');
                                }
                              }}
                              placeholder={t('Select time')}
                              style={{ width: '100%' }}
                            />
                          </div>
                          <div style={{ marginBottom: 20 }}>
                            <label>{t('Schedule Day and Month (optional)')}</label>
                            <DatePicker
                              format="d MMM"
                              placement="auto"
                              value={
                                cron.schedule.dayOfMonth !== '*' && cron.schedule.month !== '*'
                                  ? new Date(
                                      2025,
                                      Number(cron.schedule.month) - 1,
                                      Number(cron.schedule.dayOfMonth)
                                    )
                                  : null
                              }
                              onChange={value => {
                                if (value) {
                                  updateCron(
                                    cron.id,
                                    'schedule.dayOfMonth',
                                    value.getDate().toString()
                                  );
                                  updateCron(
                                    cron.id,
                                    'schedule.month',
                                    (value.getMonth() + 1).toString()
                                  );
                                } else {
                                  updateCron(cron.id, 'schedule.dayOfMonth', '*');
                                  updateCron(cron.id, 'schedule.month', '*');
                                }
                              }}
                              placeholder={t('Select day and month (if needed)')}
                              style={{ width: '100%' }}
                              ranges={[]}
                              cleanable={true}
                            />
                          </div>
                          <div style={{ marginBottom: 20 }}>
                            <Toggle
                              checked={cron.enabled}
                              onChange={(value: boolean) => updateCron(cron.id, 'enabled', value)}
                              checkedChildren={t('Enabled')}
                              unCheckedChildren={t('Disabled')}
                            />
                          </div>
                          {index > 0 && (
                            <IconButton
                              icon={<TrashIcon />}
                              onClick={() => togglePanel(cron.id)}
                              appearance="subtle"
                              size="sm"
                              style={{ marginTop: 10 }}
                            >
                              {t('Delete Notification')}
                            </IconButton>
                          )}
                        </Panel>
                      )
                  )}
                  <div style={{ marginBottom: 10 }}>
                    <IconButton
                      icon={<PlusIcon />}
                      onClick={() => addCron(type)}
                      appearance="subtle"
                      size="sm"
                    >
                      {t('Add Notification')}
                    </IconButton>
                  </div>
                </div>
              )}
            </>
          )}
        </Drawer.Body>
      </Drawer>
    </>
  );
};

export default NotificationSettingsDrawer;
